"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { formatCurrency } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CarDetails, getSimilarCars } from "@/lib/actions/car-details";
import { CarIcon, ExternalLink, FilterX, SearchX, ArrowRight, GaugeCircle, Fuel, Cog, Frown } from "lucide-react";
import { Car } from "@/db/schema";

interface SimilarCarsProps {
  currentCarId: string;
  makeId?: string;
  modelId?: string;
  typeId?: string;
}

export function SimilarCars({ currentCarId, makeId, modelId, typeId }: SimilarCarsProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarCars, setSimilarCars] = useState<Partial<CarDetails>[]>([]);

  useEffect(() => {
    const loadSimilarCars = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getSimilarCars(currentCarId, makeId, modelId, typeId);
        
        if (response.success && response.data) {
          setSimilarCars(response.data as Partial<CarDetails>[]);
        } else {
          setSimilarCars([]);
          if (!response.success) {
             const userFriendlyError = "We couldn't find similar vehicles at this time.";
             setError(userFriendlyError);
             console.error("Error from getSimilarCars:", response.error);
          }
        }
      } catch (err: any) {
        console.error("Error fetching similar cars:", err);
        setError("Unable to load similar vehicles. Please try again later.");
        setSimilarCars([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSimilarCars();
  }, [currentCarId, makeId, modelId, typeId]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  const getFirstImage = (car: Partial<CarDetails>) => {
    const mediaList = car.carMedia || car.media;
    if (!mediaList || mediaList.length === 0) {
      return null;
    }
    const featuredImage = mediaList.find(m => m.isFeatured);
    return featuredImage?.url || mediaList[0]?.url;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold tracking-tight">
          Similar Vehicles
        </h2>
        <Link 
          href="/search" 
          className="text-primary hover:underline hover:text-primary/90 transition flex items-center gap-1 text-sm"
        >
          View all
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col sm:flex-row bg-card border border-border rounded-lg overflow-hidden shadow-sm">
              <div className="relative h-48 sm:h-auto sm:w-1/3 sm:min-w-[180px]">
                <Skeleton className="h-full w-full absolute" />
              </div>
              <div className="p-4 sm:p-5 flex-1">
                <Skeleton className="h-5 w-2/3 mb-2" />
                <Skeleton className="h-7 w-1/3 mb-4" />
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : similarCars && similarCars.length > 0 ? (
        <div className="space-y-4">
          {similarCars.map((car) => {
            const mainImage = getFirstImage(car) || "https://via.placeholder.com/640x480?text=No+Image";
            
            const price = car.carPricing?.sellingPrice || (car.pricing?.sellingPrice || 0);
            const msrp = car.carPricing?.msrp || car.pricing?.msrp;
            const discount = msrp && price && msrp > price ? ((msrp - price) / msrp * 100).toFixed(0) : null;
            
            const carMake = typeof car.make === 'object' ? car.make?.name : car.make;
            const carModel = typeof car.model === 'object' ? car.model?.name : car.model;

            const mileage = car.carSpecification?.mileage || car.specification?.mileage || 'N/A';
            const mileageUnit = car.carSpecification?.mileageUnit || car.specification?.mileageUnit || 'km';
            const fuelType = car.carSpecification?.fuelType || car.specification?.fuelType || 'N/A';
            const transmission = car.carSpecification?.transmissionType || car.specification?.transmissionType || 'N/A';

            const formattedMileage = mileage === 'N/A' 
              ? 'N/A' 
              : `${typeof mileage === 'number' ? mileage.toLocaleString() : mileage} ${mileageUnit}`;

            return (
              <Link 
                key={car.id}
                href={`/cars/${car.slug}`}
                className="group flex flex-col sm:flex-row bg-card hover:bg-card/80 transition-colors border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md"
              >
                <div className="relative h-48 sm:h-auto sm:w-1/3 sm:min-w-[180px] overflow-hidden">
                  <Image
                    src={mainImage}
                    alt={`${carMake || 'Vehicle'} ${carModel || ''}`.trim()}
                    fill
                    className="object-cover transition-transform group-hover:scale-105"
                    sizes="(max-width: 640px) 100vw, 33vw"
                  />
                  
                  {discount && parseInt(discount) > 0 && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded">
                      {discount}% Off
                    </div>
                  )}
                </div>
                
                <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors">
                      {`${car.year || ''} ${carMake || ''} ${carModel || ''}`.trim()}
                    </h3>
                    
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(price)}
                      </span>
                      {msrp && msrp > price && (
                        <span className="text-sm text-muted-foreground line-through">
                          {formatCurrency(msrp)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-border">
                     <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5 truncate">
                           <GaugeCircle className="h-3.5 w-3.5 flex-shrink-0" />
                           <span className="truncate">
                              {formattedMileage}
                           </span>
                        </div>
                        <div className="flex items-center gap-1.5 truncate">
                           <Fuel className="h-3.5 w-3.5 flex-shrink-0" />
                           <span className="truncate">{fuelType}</span>
                        </div>
                        <div className="flex items-center gap-1.5 truncate">
                           <Cog className="h-3.5 w-3.5 flex-shrink-0" />
                           <span className="truncate">{transmission}</span>
                        </div>
                     </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : error ? (
        <div className="text-center py-12 border rounded-xl bg-destructive/10 text-destructive">
          <SearchX className="h-10 w-10 text-destructive/60 mx-auto mb-3" />
          <h3 className="text-lg font-medium">Error Loading Vehicles</h3>
          <p className="max-w-md mx-auto mt-1 text-sm">
            {error}
          </p>
        </div>
      ) : (
        <div className="text-center py-12 border rounded-xl bg-muted/10">
          <Frown className="h-10 w-10 text-muted-foreground/60 mx-auto mb-3" />
          <h3 className="text-lg font-medium">No Similar Vehicles Found</h3>
          <p className="text-muted-foreground max-w-md mx-auto mt-1">
            We couldn't find any similar vehicles right now. Try broadening your search or check back later.
          </p>
          <Button asChild className="mt-6">
            <Link href="/search">Browse All Vehicles</Link>
          </Button>
        </div>
      )}
    </div>
  );
} 